import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Package, Cloud, Zap, CheckCircle } from "lucide-react";

const ShippingPolicy = () => {
  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Modern Navigation */}
      <nav className="bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex justify-start items-center">
          <Link to="/" className="flex items-center gap-2 group">
            <img src="/VetriaAI.png" alt="VetriaAI" className="h-8 w-8 sm:h-9 sm:w-9" />
            <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-primary via-red-700 to-primary bg-clip-text text-transparent group-hover:from-red-800 group-hover:via-primary group-hover:to-red-800 transition-all duration-300">
              VetriaAI
            </span>
          </Link>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative py-8 sm:py-12 md:py-16 px-4 sm:px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-red-50 via-white to-blue-50 opacity-50"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(185,28,28,0.1),transparent_50%)]"></div>

        <div className="relative max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-primary/10 rounded-full mb-4 sm:mb-6">
            <Cloud className="w-8 h-8 sm:w-10 sm:h-10 text-primary" />
          </div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
            Digital Delivery Policy
          </h1>
          <p className="text-sm sm:text-base text-gray-600">
            Last Updated: October 9, 2025
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-8 sm:py-12 bg-gradient-to-b from-white to-gray-50 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-card shadow-card border border-gray-100 p-6 sm:p-8 md:p-10">
            <div className="prose prose-sm sm:prose-base max-w-none">
              <div className="space-y-6 sm:space-y-8 text-gray-700 leading-relaxed">

                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Package className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                    Digital Service Delivery
                  </h2>
                  <p className="text-sm sm:text-base mb-4">
                    VetriaAI is a cloud-based Software-as-a-Service (SaaS) platform. We do not ship physical goods.
                    All services are delivered digitally through our web application and APIs.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Zap className="w-5 h-5 text-primary" />
                    1. Access & Delivery
                  </h3>
                  <div className="space-y-3">
                    <p className="text-sm sm:text-base">
                      After purchase or trial activation, account administrators receive:
                    </p>
                    <ul className="list-disc list-inside space-y-2 text-sm sm:text-base ml-2">
                      <li>Email confirmation with account credentials and access instructions</li>
                      <li>Immediate in-app access to all subscribed features</li>
                      <li>Welcome email with onboarding resources and documentation</li>
                    </ul>
                    <p className="text-sm sm:text-base mt-3">
                      <strong>Digital Deliverables Include:</strong>
                    </p>
                    <ul className="list-disc list-inside space-y-2 text-sm sm:text-base ml-2">
                      <li>Intelligent test generation and assessment tools</li>
                      <li>Candidate screening and analytics dashboards</li>
                      <li>Verification badges and certificates (digital)</li>
                      <li>Downloadable reports and analytics (accessible via secure links or in-app export)</li>
                    </ul>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3">2. Provisioning Times</h3>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm sm:text-base font-medium mb-2">Self-Serve Plans:</p>
                      <p className="text-sm sm:text-base ml-2">
                        Typically instant after payment confirmation. Access is granted immediately upon successful payment processing.
                      </p>
                    </div>
                    <div>
                      <p className="text-sm sm:text-base font-medium mb-2">Enterprise Workspaces:</p>
                      <p className="text-sm sm:text-base ml-2">
                        Within 1-3 business days after contract countersignature and domain verification.
                        Our team will contact you to set up your workspace and provide onboarding assistance.
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3">3. Documents & Invoices</h3>
                  <p className="text-sm sm:text-base">
                    Invoices and receipts are sent to the billing email address provided during signup and are also
                    available in the billing portal. All documents are delivered digitally in PDF format.
                    You can download invoices anytime from your account dashboard.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-primary" />
                    4. Service Availability
                  </h3>
                  <div className="space-y-3">
                    <p className="text-sm sm:text-base">
                      <strong>Target Uptime:</strong> 99.9% monthly availability
                    </p>
                    <p className="text-sm sm:text-base">
                      <strong>Monitoring:</strong> 24/7 monitoring and automatic failover to ensure continuous service availability
                    </p>
                    <p className="text-sm sm:text-base">
                      <strong>Maintenance:</strong> Planned maintenance is communicated in advance via email and in-app notifications
                      whenever possible. We schedule maintenance during low-traffic hours to minimize disruption.
                    </p>
                    <p className="text-sm sm:text-base">
                      <strong>Status Updates:</strong> For real-time service status and incident updates, please contact our support team or check the Help Center.
                    </p>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3">5. Failed Delivery</h3>
                  <p className="text-sm sm:text-base mb-2">
                    If you don't receive access within expected timelines, please:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-sm sm:text-base ml-2">
                    <li>Check your spam/junk folder for the welcome email</li>
                    <li>Verify the email address used during signup</li>
                    <li>Contact us at <a href="mailto:info@vetriaai.com" className="text-primary hover:underline">info@vetriaai.com</a> with your order details</li>
                    <li>Include your order/invoice ID, email address, and date of purchase</li>
                  </ul>
                  <p className="text-sm sm:text-base mt-3">
                    We will investigate and resolve access issues within 24-48 hours during business days.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3">6. Data Security & Backup</h3>
                  <p className="text-sm sm:text-base">
                    All data is stored securely in the cloud with regular backups. Your data is protected with encryption
                    at rest and in transit. We maintain redundant systems to ensure data availability and recovery in case
                    of any technical issues.
                  </p>
                </div>

                <div className="bg-primary/5 border-l-4 border-primary p-4 sm:p-6 rounded-r-lg">
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3">Contact Information</h3>
                  <p className="text-sm sm:text-base mb-2">
                    For delivery-related inquiries:
                  </p>
                  <p className="text-sm sm:text-base mb-2">
                    <strong>Email:</strong> <a href="mailto:info@vetriaai.com" className="text-primary hover:underline">info@vetriaai.com</a>
                  </p>
                  <p className="text-sm sm:text-base">
                    <strong>Address:</strong> H. No. 5-95/1, Flat No. G, Mokkapati Towers, Fathenagar Colony,
                    K.v.rangareddy, Balanagar, Telangana, India, 500018
                  </p>
                </div>

              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 sm:py-12 px-4 sm:px-6 mt-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center text-gray-400">
            <p className="text-sm sm:text-base">&copy; 2025 VetriaAI. All copyrights are reserved by VetriaAI.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ShippingPolicy;
